import type { ComponentType } from "react";
import { lazyRetry } from "./lazyRetry";

const loaders = {
  city: () => import("@/pages/geo/CityGuidePage"),
  model: () => import("@/pages/geo/ModelGuidePage"),
  useCase: () => import("@/pages/geo/UseCaseGuidePage"),
  comparison: () => import("@/pages/geo/ComparisonGuidePage"),
  hire: () => import("@/pages/geo/HireLandingPage"),
  modelsIndex: () => import("@/pages/geo/GeoIndexPages").then(m => ({ default: m.ModelsIndexPage })),
  citiesIndex: () => import("@/pages/geo/GeoIndexPages").then(m => ({ default: m.CitiesIndexPage })),
  useCasesIndex: () => import("@/pages/geo/GeoIndexPages").then(m => ({ default: m.UseCasesIndexPage })),
  comparisonsIndex: () => import("@/pages/geo/GeoIndexPages").then(m => ({ default: m.ComparisonsIndexPage })),
};
type GeoKind = keyof typeof loaders;
const eager = new Map<GeoKind, ComponentType>();
const pending = new Map<GeoKind, Promise<void>>();
const lazy = new Map<GeoKind, ComponentType>();
for (const kind of Object.keys(loaders) as GeoKind[]) lazy.set(kind, lazyRetry(loaders[kind]));
const indexes: Record<string, GeoKind> = { "/models": "modelsIndex", "/cities": "citiesIndex",
  "/hire": "useCasesIndex", "/compare": "comparisonsIndex" };

/** The cache holds module definitions only, never request state or source data. */
export function loadEagerGeoPage(pathname: string): Promise<void> | undefined {
  const path = pathname.replace(/\/$/, "");
  const kind: GeoKind | undefined = indexes[path] ||
    (/\/cities\/[^/]+$/.test(path) ? "city" :
      /^\/models\/[^/]+$/.test(path) ? "model" :
      /^\/hire\/[^/]+$/.test(path) ? "useCase" :
      /^\/compare\/[^/]+$/.test(path) ? "comparison" :
      /\/(?:hire\/|wedding-car-hire|bryllupsbil)/.test(path) ? "hire" : undefined);
  if (!kind) return;
  if (!pending.has(kind)) pending.set(kind, loaders[kind]().then(module => {
    eager.set(kind, module.default);
  }).catch(error => { pending.delete(kind); throw error; }));
  return pending.get(kind);
}
function GeoRoute({ kind }: { kind: GeoKind }) { const Page = eager.get(kind) || lazy.get(kind)!; return <Page />; }
export function PublicCityGuideRoute() { return <GeoRoute kind="city" />; }
export function PublicModelGuideRoute() { return <GeoRoute kind="model" />; }
export function PublicUseCaseGuideRoute() { return <GeoRoute kind="useCase" />; }
export function PublicComparisonGuideRoute() { return <GeoRoute kind="comparison" />; }
export function PublicHireLandingRoute() { return <GeoRoute kind="hire" />; }
export function PublicModelsIndexRoute() { return <GeoRoute kind="modelsIndex" />; }
export function PublicCitiesIndexRoute() { return <GeoRoute kind="citiesIndex" />; }
export function PublicUseCasesIndexRoute() { return <GeoRoute kind="useCasesIndex" />; }
export function PublicComparisonsIndexRoute() { return <GeoRoute kind="comparisonsIndex" />; }
